import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useToast } from "@/app/providers/ToastProvider";
import { addDownload } from "@/features/download/api";
import {
  searchMedia,
  searchTorrentsByMedia,
  searchTorrentsByMediaStream,
  subscribeFromMedia,
  type MediaSearchItem,
  type SearchContext,
} from "@/features/search/api";
import { formatBytes, mediaIdOf } from "@/shared/lib/format";
import { proxyImageUrl } from "@/shared/lib/image";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { Input } from "@/shared/ui/Input";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

/** 搜索：媒体 → 订阅 / 搜种(SSE) → 下载 */
export function SearchPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const deepMedia = params.get("media");
  const abortRef = useRef<AbortController | null>(null);

  const [query, setQuery] = useState(initialQ);
  const [medias, setMedias] = useState<MediaSearchItem[]>([]);
  const [selected, setSelected] = useState<MediaSearchItem | null>(null);
  const [torrents, setTorrents] = useState<SearchContext[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [torrentLoading, setTorrentLoading] = useState(false);
  const [progressText, setProgressText] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const selectedId = useMemo(
    () => (selected ? mediaIdOf(selected) : deepMedia),
    [selected, deepMedia],
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function runMediaSearch(title: string) {
    const q = title.trim();
    if (!q) return;
    if (q.length > 120) {
      toast("关键词过长", "error");
      return;
    }
    abortRef.current?.abort();
    setMediaLoading(true);
    setTorrents([]);
    setSelected(null);
    setProgressText(null);
    try {
      const list = await searchMedia(q);
      setMedias(Array.isArray(list) ? list : []);
      setParams({ q }, { replace: true });
      if (!list?.length) toast("未找到相关媒体", "info");
    } catch (e) {
      toast(e instanceof Error ? e.message : "搜索失败", "error");
    } finally {
      setMediaLoading(false);
    }
  }

  async function runTorrentSearch(media: MediaSearchItem) {
    const id = mediaIdOf(media);
    if (!id) {
      toast("该媒体缺少可用 ID", "error");
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setSelected(media);
    setTorrentLoading(true);
    setTorrents([]);
    setProgressText("连接搜索流…");
    setParams(
      { q: query || media.title || "", media: id },
      { replace: true },
    );

    const opts = {
      mtype: media.type || undefined,
      season: media.season,
    };

    try {
      await searchTorrentsByMediaStream(id, {
        ...opts,
        signal: ac.signal,
        onProgress: (text) => setProgressText(text),
        onItems: (items, mode) => {
          setTorrents((prev) =>
            mode === "append" ? [...prev, ...items] : items,
          );
        },
      });
      setTorrents((prev) => {
        if (!prev.length) toast("未搜到种子", "info");
        return prev;
      });
    } catch (e) {
      if (ac.signal.aborted) return;
      // SSE 不可用时回退一次性接口
      try {
        setProgressText("流式搜索不可用，改用完整搜索…");
        const list = await searchTorrentsByMedia(id, opts);
        setTorrents(Array.isArray(list) ? list : []);
        if (!list?.length) toast("未搜到种子", "info");
      } catch (e2) {
        toast(e2 instanceof Error ? e2.message : "搜种失败", "error");
      }
    } finally {
      if (!ac.signal.aborted) {
        setTorrentLoading(false);
        setProgressText(null);
      }
    }
  }

  useEffect(() => {
    if (initialQ) {
      void runMediaSearch(initialQ);
    }
    // 首屏仅消费进入时的 query
  }, []);

  async function onSubscribe(media: MediaSearchItem) {
    const key = `sub-${mediaIdOf(media)}`;
    setBusyKey(key);
    try {
      await subscribeFromMedia(media);
      toast("订阅成功", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "订阅失败", "error");
    } finally {
      setBusyKey(null);
    }
  }

  async function onDownload(ctx: SearchContext, index: number) {
    const torrent = ctx.torrent_info;
    const media = ctx.media_info || selected;
    if (!torrent?.enclosure) {
      toast("种子缺少下载链接", "error");
      return;
    }
    if (!media) {
      toast("缺少媒体信息", "error");
      return;
    }
    const key = `dl-${index}`;
    setBusyKey(key);
    try {
      await addDownload(
        {
          type: media.type,
          title: media.title,
          year: media.year,
          tmdb_id: media.tmdb_id,
          douban_id: media.douban_id,
          bangumi_id: media.bangumi_id,
          poster_path: media.poster_path,
        },
        {
          site: torrent.site,
          site_name: torrent.site_name,
          title: torrent.title,
          description: torrent.description,
          enclosure: torrent.enclosure,
          page_url: torrent.page_url,
          size: torrent.size,
          seeders: torrent.seeders,
          peers: torrent.peers,
          labels: torrent.labels,
          volume_factor: torrent.volume_factor,
        },
      );
      toast("已添加下载", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "添加下载失败", "error");
    } finally {
      setBusyKey(null);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void runMediaSearch(query);
  }

  return (
    <div>
      <PageHeader title="搜索" description="媒体识别 → 订阅 / 搜种 → 下载" />

      <form onSubmit={onSubmit} className="mb-5 flex max-w-xl gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="片名、剧名…"
          maxLength={120}
          autoFocus
        />
        <Button type="submit" disabled={mediaLoading}>
          {mediaLoading ? "搜索中…" : "搜索"}
        </Button>
      </form>

      {mediaLoading ? (
        <div className="mb-4 flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 正在搜索媒体…
        </div>
      ) : null}

      {medias.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-2 text-[13px] font-medium text-fg-muted">媒体结果</h2>
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {medias.map((m, i) => {
              const id = mediaIdOf(m) || `m-${i}`;
              const active = selectedId === mediaIdOf(m);
              return (
                <li
                  key={id}
                  className={`flex gap-3 rounded-[var(--radius-md)] border bg-bg-card p-3 ${
                    active ? "border-brand/50" : "border-border"
                  }`}
                >
                  <div className="h-[96px] w-[64px] shrink-0 overflow-hidden rounded-[var(--radius-sm)] bg-bg-hover">
                    {proxyImageUrl(m.poster_path) ? (
                      <img
                        src={proxyImageUrl(m.poster_path)}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-[14px] font-medium">
                      {m.title || "未知"}
                    </h3>
                    <p className="text-[12px] text-fg-subtle">
                      {[
                        m.year,
                        m.type,
                        m.vote_average ? `★ ${m.vote_average}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {mediaIdOf(m) ? (
                        <Link
                          to={`/media/${encodeURIComponent(mediaIdOf(m)!)}?type=${encodeURIComponent(m.type || "电影")}`}
                        >
                          <Button variant="ghost">详情</Button>
                        </Link>
                      ) : null}
                      <Button
                        variant="ghost"
                        disabled={busyKey === `sub-${mediaIdOf(m)}`}
                        onClick={() => void onSubscribe(m)}
                      >
                        订阅
                      </Button>
                      <Button onClick={() => void runTorrentSearch(m)}>
                        搜种子
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {selected || torrentLoading || torrents.length > 0 ? (
        <section>
          <h2 className="mb-2 text-[13px] font-medium text-fg-muted">
            种子
            {selected?.title ? ` · ${selected.title}` : ""}
            {torrents.length ? ` · ${torrents.length}` : ""}
          </h2>
          {torrentLoading || progressText ? (
            <div className="mb-2 flex items-center gap-2 text-[13px] text-fg-muted">
              {torrentLoading ? <Spinner /> : null}
              {progressText || "正在搜索站点资源…"}
            </div>
          ) : null}
          {!torrentLoading && torrents.length === 0 ? (
            <EmptyState title="暂无种子结果" />
          ) : null}
          <ul className="flex flex-col gap-2">
            {torrents.map((ctx, index) => {
              const t = ctx.torrent_info;
              if (!t) return null;
              return (
                <li
                  key={`${t.enclosure || t.title}-${index}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-md)] border border-border bg-bg-card px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-fg">
                      {t.title || "未命名种子"}
                    </div>
                    <div className="mt-0.5 text-[12px] text-fg-subtle">
                      {[
                        t.site_name,
                        formatBytes(t.size),
                        t.seeders != null ? `↑${t.seeders}` : null,
                        t.volume_factor,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <Button
                    disabled={busyKey === `dl-${index}`}
                    onClick={() => void onDownload(ctx, index)}
                  >
                    下载
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {!mediaLoading && medias.length === 0 && !query ? (
        <EmptyState
          title="输入片名开始搜索"
          description="支持电影与剧集；搜到后可订阅或直接下种子"
        />
      ) : null}
    </div>
  );
}
